import { action, computed, observable } from 'mobx';
import { IBopisData } from '../../api/IDataMappings';
import Bopis from '../../domain/entities/Bopis/Bopis';
import PhoneFormStore, { IAddressFormStoreOptions } from '../../domain/entities/AddressFormStore/PhoneFormStore';
import Config from '../../domain/entities/Config/Config';
import { formatPhoneIntoE164 } from '../../utils/AddressFormUtils';

export interface IBopisFormValues {
  countryId?: number;
  phone: string;
  email: string;
  lastName: string;
  firstName: string;
}

export default class BopisFormInfo {
  @observable purchaserFormStore: PhoneFormStore<IBopisFormValues>;

  @observable proxyFormStore: PhoneFormStore<IBopisFormValues>;

  @observable isPurchaserPicking = true;

  @observable doorId: string;

  @observable countryId: number;

  @observable isReady = false;

  @observable config: Config;

  constructor(config: Config) {
    const formStoreOptions: IAddressFormStoreOptions<IBopisFormValues> = {
      initialValues: {
        countryId: undefined,
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
      },
    };
    this.config = config;
    this.purchaserFormStore = new PhoneFormStore(config, formStoreOptions);
    this.proxyFormStore = new PhoneFormStore(config, formStoreOptions);
  }

  @action
  init(values: Bopis): void {
    if (values) {
      const { purchaserPickupInfo, proxyPickupInfo, doorId } = values;

      this.doorId = doorId;
      this.purchaserFormStore.setValues({
        ...purchaserPickupInfo,
        countryId: this.countryId,
      });
      this.proxyFormStore.setValues({
        ...proxyPickupInfo,
        countryId: this.countryId,
      });

      /**
       *  When the new format/phone input is enabled we have to support legacy phone values.
       *  Try to parse and format as E164 format and mark formStore so we enable the new phone input. 
       */
      if (this.config.useE164PhoneStandard) {
        this.purchaserFormStore.values.phone = formatPhoneIntoE164(purchaserPickupInfo?.phone || '', this.countryId);
        this.proxyFormStore.values.phone = formatPhoneIntoE164(proxyPickupInfo?.phone || '', this.countryId);
      }

      if (values.proxyPickupInfo && values.proxyPickupInfo.email.length) {
        this.isPurchaserPicking = false;
      }
    }
  }

  get values(): IBopisData {
    const { values: purchaserFormValues } = this.purchaserFormStore;
    let data: IBopisData;
    data = {
      doorId: this.doorId,
      proxyMode: false,
      purchaserEmail: purchaserFormValues.email,
      purchaserFirstName: purchaserFormValues.firstName,
      purchaserLastName: purchaserFormValues.lastName,
      purchaserPhone: purchaserFormValues.phone,
    };

    if (!this.isPurchaserPicking) {
      const { values: proxyFormValues } = this.proxyFormStore;
      data = {
        ...data,
        proxyEmail: proxyFormValues.email,
        proxyFirstName: proxyFormValues.firstName,
        proxyLastName: proxyFormValues.lastName,
        proxyMode: true,
        proxyPhone: proxyFormValues.phone,
      };
    }

    return data;
  }

  @computed
  get isValid(): boolean {
    return this.isReady
      && this.purchaserFormStore.isValid
      && (this.isPurchaserPicking || this.proxyFormStore.isValid);
  }
}
